import React, {useState, useCallback, useEffect, useMemo} from "react";

import {useMediaQuery} from "@churchofjesuschrist/eden-media-query";
import { Normalize } from "@churchofjesuschrist/eden-normalize";
import { PlatformModal } from "@churchofjesuschrist/eden-platform-modal";
import { useNavigate } from "react-router-dom";

import BreadcrumbContext from "./breadcrumb-context";
import Desktop from "./desktop/desktop";
import LanguageContext from "./language-context";
import LoadingSpinnerContext from "./loading-spinner-context";
import urlRoutingService from "./routing-service";
import usePwa from "./use-pwa";
import UserAlertDismissTriggerContext from "./user-alert-dismiss-trigger-context";
import impersonateHelper, {
  getImpersonatingEmployee,
  setEmployeeImpersonate as persistImpersonatingForFetchWrapper,
  clearEmployeeImersonate as clearImpersonationForFetchWrapper
} from "../../app/impersonate-service";
import programTreeService from "../../app/program-tree-service";
import FeatureFlags from "../../generic/feature-flags/feature-flags";
import useSwitch from "../../generic/hooks/use-switch/use-switch";
import { PreferencesProvider } from "../../generic/local-stored-preferences-provider/local-stored-preferences-provider";
import NavigationContext from "../../shared/navigation-link/navigation-context";
import UserPermissionsContext from "../../utils/permissions/user-permissions-context";
import StringsProvider from "../../utils/strings/strings-provider";
import IosPwaModal from "../ios-pwa-modal/ios-pwa-modal";
import MobileLayout from "../mobile/mobile-layout/mobile-layout";
import PwaBanner from "../pwa-banner/pwa-banner";
import TimeoutCounter from "../timeout-counter/timeout-counter";
//import SiteFooter from "../site-footer/site-footer";
import { TwilioProvider } from "../twilio-provider/twilio-provider";
import UnsavedChangesConfirm from "../unsaved-changes-confirm/unsaved-changes-confirm";


const defaultObj = {};
const defaultAlerts = { systemAlerts:[]};
const defaultFunc = () => {}
const defaultArray = [];
const defaultProgramService = new programTreeService();
const defaultEmployeeService = new impersonateHelper();
const getEffectiveTwilioUserIdentifier = (user, impersonating) =>
  impersonating?.churchAccountId ?? user?.churchAccountId ?? user?.name;

const Layout = ({
  programService = defaultProgramService,
  employeeService = defaultEmployeeService,
  routingService:propsRoutingService,
  language = "en",
  strings = defaultObj,
  user:propsUser = {name:"anonymous"},
  userAlerts = defaultAlerts,
  onUserAlertDeleted = defaultFunc,
  navigation = defaultObj,
  lean = false,
  warned = false,
  printMode = false,
  confirmedPermissions = defaultArray,
  onImpersonationChange = defaultFunc,
  title,
  children,
  inactivityMinutes = 58,
  changeLanguage = defaultFunc,
  navigationStatus = defaultObj,
  setNavigationLoading = defaultFunc,
  clearNavigationStatus = defaultFunc,
  twilioService,
}) => {

  const routerNavigate = useNavigate();
  const routingService = useMemo(() => {
    if (propsRoutingService) {
      return propsRoutingService;
    }
    return urlRoutingService(routerNavigate);
  },[propsRoutingService,routerNavigate])

  //NOTE: navigation isn't kept in state because any change to navigation
  //is actually a redirect.  So it is just a property value as far as this component is concerned.
  const [menuOpen, setMenuOpen] = useState(false);
  const [programFound, setProgramFound] = useState(null);
  const [employeeFound, setEmployeeFound] = useState(null);
  const [showEmployeeSearch, activateEmployeeSearch,deactivateEmployeeSearch] = useSwitch(false);
  const [confirmFunc,setConfirmFunc] = useState(null);

  useEffect(() => {
    if (warned) {
      setMenuOpen(false);
    }
    else if (!lean) {
      setMenuOpen(true);
    }
  },[warned, lean])

  const askUserToConfirmBeforeProceeding = useCallback((doWhenConfirmed) => {
    if (!warned) {
      doWhenConfirmed();
    } else {
      setConfirmFunc({after:doWhenConfirmed});
    }

  },[warned]);
  const userConfirmAccepted = useCallback(() => {
    if (!warned) {return;}
    setConfirmFunc(obj => {
      if (typeof obj?.after === "function") {
        obj.after();
      }
      return null;
    })
  },[warned]);
  const userConfirmDeclined = useCallback(() => {
    if (!warned) {return;}
    setConfirmFunc(null);
  },[warned]);

  const user = useMemo(() => propsUser ?? {name:"anonymous"}, [propsUser]);

  const [impersonating, setImpersonating] = useState(() => getImpersonatingEmployee());

  const menuClick = useCallback(() => {
    setMenuOpen(o => !o);
    setProgramFound(null);
    setEmployeeFound(null);
    clearNavigationStatus();
  },[clearNavigationStatus]);

  const programSearched = useCallback(async(programNumber, clearCallback) => {
    const previousProgramNum = navigation?.program?.programNumber;
    setNavigationLoading();
    let loadedNavigation = null;
    try {
      loadedNavigation = await programService.searchByProgramNumber(programNumber);
      if (!loadedNavigation || !loadedNavigation.area || loadedNavigation.area.length === 0) {
        clearNavigationStatus();
        setEmployeeFound(false);
        setProgramFound(false);
      }
      if (typeof clearCallback === "function") {
        clearCallback();
      }
      routingService.redirectToNavigation(loadedNavigation);

      if (previousProgramNum === programNumber.toString()) {
        clearNavigationStatus();
      }
    } catch (e) {
      loadedNavigation = null;
    }
  },[navigation, setNavigationLoading, programService, routingService, clearNavigationStatus])


  const clearImpersonation = useCallback(() => {
    askUserToConfirmBeforeProceeding(() => {
      clearNavigationStatus();
      setImpersonating(null);
      clearImpersonationForFetchWrapper();
      onImpersonationChange();
    })
  },[clearNavigationStatus, askUserToConfirmBeforeProceeding, onImpersonationChange]);

  const employeeSearched = useCallback((employeeId) => {
    askUserToConfirmBeforeProceeding(async () => {
      setNavigationLoading();
      setEmployeeFound(null);
      try {
        const employeeData = await employeeService.checkEmployee(employeeId);
        clearNavigationStatus();
        if (!employeeData || !employeeData.churchAccountId) {
          setEmployeeFound(false);
          setProgramFound(null);
          clearImpersonationForFetchWrapper();
        } else {
          setImpersonating(employeeData);
          persistImpersonatingForFetchWrapper(employeeData);
          onImpersonationChange();
          deactivateEmployeeSearch();
        }
      } catch (e) {
        //todo:error state?
      }
    })
  },[askUserToConfirmBeforeProceeding,
    setNavigationLoading,
    employeeService,
    clearNavigationStatus,
    deactivateEmployeeSearch,
    onImpersonationChange]);

  const logOutClicked = useCallback(() => askUserToConfirmBeforeProceeding(() => routingService.logOut()),
    [routingService, askUserToConfirmBeforeProceeding]);

  const helpClicked = useCallback(() => askUserToConfirmBeforeProceeding(() => routingService.help()),
    [askUserToConfirmBeforeProceeding, routingService]);
  const languageChosen = useCallback((code) => {
    changeLanguage(code);
    //setMenuOpen(false);
  },[changeLanguage]);

  const homeClicked = useCallback(() => askUserToConfirmBeforeProceeding(() => routingService.home()),
    [askUserToConfirmBeforeProceeding, routingService]);

  const adminClicked = useCallback(() => askUserToConfirmBeforeProceeding(() => routingService.admin()),
    [askUserToConfirmBeforeProceeding, routingService]);

  const userAlertsClicked = useCallback (() => askUserToConfirmBeforeProceeding(() => routingService.userAlerts(navigation)),
    [askUserToConfirmBeforeProceeding, navigation, routingService]);

  const navigationLevelClicked = useCallback((area,region,coordinator,program,year,term) => {
    askUserToConfirmBeforeProceeding(async () => {
      const isAddYear = year?.id === -1;
      if (isAddYear) {
        const navToDirectTo = {
          area,region,coordinator,program,year,term,
          isAddYear: year?.id === -1
        };
        routingService.redirectToNavigation(navToDirectTo);
        return;
      }

      const areaId = area ? area.id : undefined;
      const coordinatorId = coordinator ? coordinator.id : undefined;
      const regionId = region ? region.id : undefined;
      const programId = program ? program.id : undefined;
      let yearId = year ? year.id : undefined;
      let termId = term ? term.id : undefined;

      try {
        setNavigationLoading();
        const newNavigation = await programService.getNavigation(areaId,regionId,coordinatorId,programId,yearId,termId);
        newNavigation.isAddYear = isAddYear;
        clearNavigationStatus();
        routingService.redirectToNavigation(newNavigation);
      } catch (e) {
        //todo:error?
        clearNavigationStatus();
      }
    });
  },[askUserToConfirmBeforeProceeding, setNavigationLoading, clearNavigationStatus, programService, routingService]);

  const setLoadingState = useCallback((loading) => {
    if (loading) {
      setNavigationLoading()
    } else {clearNavigationStatus()}
  },[clearNavigationStatus, setNavigationLoading])



  const onWideMediaQueryMatchChange = useCallback(({matches:isWide}) => {

    setMenuOpen(open => {
      if (isWide && !open) {
        return true;
      }
      if (!isWide && open) {
        return false;
      }
      return open;
    })
  },[]);

  const onMenuClickOff = useCallback((isWide) => {
    if (!isWide) {
      setMenuOpen(false)
    }
  },[]);
  const noopMediaQueryCallback = useCallback(() => {}, []);
  const [isNotMobile] = useMediaQuery(noopMediaQueryCallback, "(min-width:800px)");
  const isMobile = useMemo(() => !isNotMobile, [isNotMobile]);

  const navigateProgramTreeObject = useMemo(() => {
    const { area, region, coordinator, program, year } = navigation;
    return {
      navigation: navigation,
      showBreadcrumb: !lean && !isMobile,
      onAreaClicked: (clickedArea) => navigationLevelClicked(clickedArea),
      onRegionClicked:(clickedRegion) => navigationLevelClicked(area,clickedRegion),
      onCoordinatorClicked:(clickedCoordinator) => navigationLevelClicked(area,region,clickedCoordinator),
      onProgramClicked:(clickedProgram) => navigationLevelClicked(area,region,coordinator,clickedProgram),
      onYearClicked:(clickedYear) => navigationLevelClicked(area,region,coordinator,program,clickedYear),
      onTermClicked:(clickedTerm) => navigationLevelClicked(area,region,coordinator,program,year,clickedTerm)
    }
  }
  ,[navigation, navigationLevelClicked, lean, isMobile]);

  const [isWide] = useMediaQuery(onWideMediaQueryMatchChange,"(min-width:800px)");
  //media query default match behavior made it so it is simplest
  //to match to not mobile on min width then inverse that for our logic

  const {area,program} = navigation;

  const barTheme = useMemo(() => program ? program.programType : area ? "area" : "",[area, program]);

  //note: we only ever ask for can admin application on our application id
  //so we just need to see if that permission exists by that name
  const canAdmin = useMemo(() => {
    const canAdminPermission = confirmedPermissions.find((x) => {
      return (
        x.permission === "CanAdminApplication"
      );
    });

    return canAdminPermission != null && canAdminPermission !== undefined;

  },[confirmedPermissions]);
  const canEditProgramSupplemental = useMemo(() => {
    const found = confirmedPermissions.find(
      (x) => x.permission === "CanEditProgramSupplemental"
    );
    return found !== null && found !== undefined;
  },[confirmedPermissions]);

  const topBarProps = useMemo(() => {
    const topBar = {
      user,
      theme:barTheme,
      alerts:userAlerts,
      lean: isMobile ? false : lean || warned,
      onUserAlertDeleted,
      canAdmin:canAdmin,
      onHomeClicked:homeClicked,
      onUserAlertClicked:userAlertsClicked,
      onAdminClicked:adminClicked,
    };
    if (!isMobile) {
      topBar.hamburgerClick = menuClick;
      topBar.strings = strings;
    }
    return topBar;
  },
  [adminClicked,
    barTheme,
    canAdmin,
    homeClicked,
    isMobile,
    lean,
    menuClick,
    onUserAlertDeleted,
    strings,
    user,
    userAlerts,
    userAlertsClicked,
    warned])

  const userOptionsProps = useMemo(() => {
    const userOptions = {
      onLogOutClicked:logOutClicked,
      onLanguageChosen:languageChosen,
      onHelpClicked:helpClicked,
      language:language
    }
    if (!isMobile) {
      userOptions.onImpersonateClicked = activateEmployeeSearch;
    }
    return userOptions;
  },[activateEmployeeSearch, helpClicked, isMobile, language, languageChosen, logOutClicked]);

  const programTreeProps = useMemo(() => {
    return {...navigateProgramTreeObject,////gives us all our clicked handlers
      canAddYear:canEditProgramSupplemental,
    }
  },[canEditProgramSupplemental, navigateProgramTreeObject]);

  const programSearchProps = useMemo(() => {
    return {
      onSearch:programSearched,
      notFound: programFound === false,
    }
  },[programFound, programSearched]);

  const impersonateProps = useMemo(() => {
    return {
      impersonating,
      onImpersonateClicked: activateEmployeeSearch,
      onCleared: clearImpersonation,
      onChanged: activateEmployeeSearch,
      onSearch: employeeSearched,
      onCancel: deactivateEmployeeSearch,
      notFound: employeeFound === false,
      showEmployeeSearch
    }
  },[impersonating,
    activateEmployeeSearch,
    clearImpersonation,
    employeeSearched,
    deactivateEmployeeSearch,
    employeeFound,
    showEmployeeSearch]);

  const showWarning = typeof confirmFunc?.after === "function" && warned;

  const {
    showPwaBanner,
    pwaDismissed,
    pwaInstallClicked,
    dismissPwa,
    closeIosModal,
    iosPwaModalActive
  } = usePwa();

  return <StringsProvider strings={strings}>
    <LanguageContext.Provider value={language}>
      <NavigationContext.Provider value={navigation}>
        <BreadcrumbContext.Provider value={navigateProgramTreeObject}>
          <LoadingSpinnerContext.Provider value={setLoadingState}>
            <UserAlertDismissTriggerContext.Provider
              value={onUserAlertDeleted}
            >
              <UserPermissionsContext.Provider value={confirmedPermissions}>
                <PreferencesProvider>
                  <TwilioProvider
                    identifier={getEffectiveTwilioUserIdentifier(user, impersonating)}
                    service={twilioService}
                  >
                    <TimeoutCounter
                      inactivityMinutes={inactivityMinutes}
                      routingService={routingService}
                    >
                      <Normalize />
                      {showWarning && (
                        <PlatformModal open={true}>
                          <UnsavedChangesConfirm strings={strings}
                            onCancel={userConfirmDeclined}
                            onConfirm={userConfirmAccepted}/>
                        </PlatformModal>
                      )}
                      {printMode ? children :
                        isMobile ? (<MobileLayout
                          topBarProps={topBarProps}
                          userOptionsProps={userOptionsProps}
                          programTreeProps={programTreeProps}
                          programSearchProps={programSearchProps}
                          navigationStatus={navigationStatus}>
                          {children}
                        </MobileLayout>) :
                          (
                            <Desktop
                              isWide={isWide}
                              navigationStatus={navigationStatus}
                              lean={lean}
                              menuOpen={menuOpen}
                              topBarProps={topBarProps}
                              userOptionsProps={userOptionsProps}
                              programTreeProps={programTreeProps}
                              programSearchProps={programSearchProps}
                              impersonateProps = {impersonateProps}
                              onMenuClickOff = {onMenuClickOff}
                              title = {title}>
                              {children}
                            </Desktop>
                          )

                      }
                      <FeatureFlags flag="PWA">
                        <div>
                          {!pwaDismissed && showPwaBanner &&
                  <PwaBanner installCallback={pwaInstallClicked}
                    closeCallback={dismissPwa}/>}
                          {iosPwaModalActive && <IosPwaModal active={iosPwaModalActive} onCancelClick={closeIosModal} />}
                        </div>
                      </FeatureFlags>
                    </TimeoutCounter>
                  </TwilioProvider>
                </PreferencesProvider>
              </UserPermissionsContext.Provider>
            </UserAlertDismissTriggerContext.Provider>
          </LoadingSpinnerContext.Provider>
        </BreadcrumbContext.Provider>
      </NavigationContext.Provider>
    </LanguageContext.Provider>
  </StringsProvider>
}

export { getEffectiveTwilioUserIdentifier };
export default Layout;
